import * as RX from "rxjs";
import { MapView } from "../../mapView/mapView";
import { ApiService } from "src/js/services/api-service";
import { Point, PointId } from "src/js/models/map";
import {
  ModuleListEvent,
  ModuleListEventClickMultiple,
  ModuleListEventClickSingle,
} from "../moduleListView";
import { insertSpinner } from "../../loadingSpinner/spinner";
import { ElementListWithDataset } from "src/js/models/module";
import { App } from "src/js/app/app";
import { pipe } from "fp-ts/lib/function";
import { none } from "fp-ts/lib/Option";
import { initializeInteractiveTooltips } from "src/js/helpers/tippy-helpers";

export type MapViewParameters = {
  apiService: ApiService;
  elementsContainer: HTMLElement;
  exhibitionId: number;
  showTooltips: boolean;
  iconValueAccessor: (m: any) => string;
};

export class ModuleListMapView {
  public readonly event$: RX.Observable<ModuleListEvent>;
  public readonly render$: RX.OperatorFunction<
    ElementListWithDataset,
    ElementListWithDataset
  >;

  private constructor(
    event$: RX.Observable<ModuleListEvent>,
    render$: RX.OperatorFunction<ElementListWithDataset, ElementListWithDataset>
  ) {
    this.event$ = event$;
    this.render$ = render$;
  }

  static MakeView(config: MapViewParameters): ModuleListMapView {
    const eventSubject$ = new RX.Subject<ModuleListEvent>();

    const onClickSingle = (pointId: PointId) => {
      const event: ModuleListEventClickSingle = {
        type: "clickSingle",
        moduleId: pointId.id,
        target: null,
      };
      eventSubject$.next(event);
    };

    const onClickMultiple = (points: Point[]) => {
      const event: ModuleListEventClickMultiple = {
        type: "clickMultiple",
        moduleIds: points.map((p) => p.id),
        locationId: points[0].locationName,
        target: null,
      };
      eventSubject$.next(event);
    };

    // Initialize tooltips with the onClickSingle callback
    initializeInteractiveTooltips(onClickSingle);

    return new ModuleListMapView(
      eventSubject$.asObservable(),
      this.#Render(config, onClickSingle, onClickMultiple)
    );
  }

  static #Render(
    config: MapViewParameters,
    onClickSingle: (pointId: PointId) => any,
    onClickMultiple: (points: Point[]) => any
  ): RX.OperatorFunction<ElementListWithDataset, ElementListWithDataset> {
    const apiService = config.apiService;

    const mapSet$ = App.getExhibitionFromId(config.exhibitionId).pipe(
      RX.map((exhibition) => {
        return exhibition.mapSet;
      })
    );

    return RX.pipe(
      RX.tap((x) => insertSpinner(config.elementsContainer)),
      RX.combineLatestWith(mapSet$),
      RX.tap(([postsWithDataset, maps]) => {
        const points = postsWithDataset.elements.flatMap((post) => {
          const locationsData = postsWithDataset.relatedModules["location"];
          const elementLocations = post.locationIds
            .map((standplads) => {
              const locationId = standplads.id;
              const locationData = locationsData.elements.find(
                (location) => location.id === locationId
              );
              if (locationData) {
                const point: Point = {
                  id: post.id,
                  x: locationData.locationData.x,
                  y: locationData.locationData.y,
                  mapId: locationData.locationData.mapId,
                  iconValue: config.iconValueAccessor(post),
                  iconClass: [
                    post.postType +
                      " hall_" +
                      locationData.locationData?.hallId +
                      " area_" +
                      locationData.locationData?.areaId,
                  ].join(""),
                  locationName: locationData.locationData.name,
                  pathCustomIcon: locationData.locationData.pathCustomIcon,
                  pathCustomIcons: locationData.locationData.pathCustomIcons,
                  elementLogo: post.imageLogo,
                  elementTitle: post.title,
                };
                return point;
              }
              return {
                id: 0,
                x: NaN, // Standardværdi for x
                y: NaN, // Standardværdi for y
                mapId: 0,
                iconValue: "",
                iconClass: "",
                locationName: "",
                pathCustomIcon: none,
                pathCustomIcons: none,
                elementLogo: "",
                elementTitle: "",
              };
            })
            .filter((point) => {
              return !isNaN(point.x) && !isNaN(point.y);
            });
          return elementLocations;
        });

        const mapElement = document.createElement("div");
        config.elementsContainer.replaceChildren(mapElement);
        MapView.render(
          mapElement,
          maps,
          points,
          config.showTooltips,
          onClickSingle,
          onClickMultiple
        );
      }),
      RX.map(([posts]) => posts)
    );
  }
}
